// Cliente completo, sustituto de `createClient()` de @supabase/supabase-js.
//
// Expone la misma superficie que usaba la aplicación —from, rpc, auth, storage,
// functions, channel— apuntando al gateway propio sobre AWS RDS.

import { AuthClient } from './auth';
import { from, QueryBuilder, rpc } from './queryBuilder';
import { StorageClient } from './storage';
import { GatewayError, request } from './transport';
import type { RealtimePostgresChangesPayload } from './types';

// ─── Edge Functions ──────────────────────────────────────────────────────────

class FunctionsClient {
  async invoke<T = any>(
    name: string,
    options?: { body?: unknown; headers?: Record<string, string> },
  ): Promise<{ data: T | null; error: { message: string; name: string } | null }> {
    try {
      const data = await request<T>(`/functions/${encodeURIComponent(name)}`, {
        method: 'POST',
        body: options?.body ?? {},
        headers: options?.headers,
      });
      return { data, error: null };
    } catch (err) {
      const message = err instanceof GatewayError ? err.message : String((err as Error)?.message ?? err);
      return { data: null, error: { name: 'FunctionsError', message } };
    }
  }
}

// ─── Realtime ────────────────────────────────────────────────────────────────

// Supabase Realtime no tiene equivalente en RDS: dependía del servidor de
// replicación de Supabase. El canal se mantiene como stub para que el código que
// lo usa no reviente; donde hacía falta refrescar en vivo (usePlans) se pasó a
// consultar cada cierto tiempo. Si algún día hace falta tiempo real, lo natural
// sería LISTEN/NOTIFY de Postgres sobre un WebSocket en el gateway.
class RealtimeChannel {
  readonly topic: string;

  constructor(topic: string) {
    this.topic = topic;
  }

  on(
    _event: string,
    _filter: Record<string, unknown>,
    _callback: (payload: RealtimePostgresChangesPayload<any>) => void,
  ): this {
    return this;
  }

  subscribe(callback?: (status: string) => void): this {
    if (callback) setTimeout(() => callback('SUBSCRIBED'), 0);
    return this;
  }

  unsubscribe(): Promise<'ok'> {
    return Promise.resolve('ok');
  }
}

// ─── Cliente ─────────────────────────────────────────────────────────────────

export class DatabaseClient {
  readonly auth = new AuthClient();
  readonly storage = new StorageClient();
  readonly functions = new FunctionsClient();

  from<T = any>(table: string): QueryBuilder<T> {
    return from<T>(table);
  }

  rpc<T = any>(fn: string, args: Record<string, unknown> = {}): QueryBuilder<T> {
    return rpc<T>(fn, args);
  }

  channel(topic: string): RealtimeChannel {
    return new RealtimeChannel(topic);
  }

  removeChannel(channel: RealtimeChannel): Promise<'ok'> {
    return channel?.unsubscribe?.() ?? Promise.resolve('ok');
  }

  removeAllChannels(): Promise<'ok'[]> {
    return Promise.resolve([]);
  }

  /** Diagnóstico: comprueba que el gateway llega a RDS. */
  async health(): Promise<{ ok: boolean; database?: unknown; error?: string }> {
    try {
      return await request('/db/health', { method: 'GET', auth: false });
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : String(err) };
    }
  }
}

let instance: DatabaseClient | null = null;

/** Instancia única: mantener una sola evita duplicar timers de refresco. */
export function createClient(): DatabaseClient {
  instance ??= new DatabaseClient();
  return instance;
}
